/**
 * The customer and partner testimonials Steve supplied on 16 September 2026,
 * transcribed verbatim.
 *
 * They live here rather than inline in each page because several of them are
 * used on more than one page, and the words are the customer's: a page that
 * paraphrased or trimmed one to fit its layout would be misquoting them. One
 * definition, one place to correct it.
 *
 * Attribution is the sector, not the company, which is how Steve supplied them.
 * When a named customer agrees to be quoted, change `attribution` here and every
 * page that uses the quote follows.
 */

export const MANUFACTURING_EASY_TO_ADMINISTER = {
  quote:
    'IMPROVE is incredibly user-friendly and very easy to administer, while our employees genuinely enjoy completing the courses.',
  attribution: 'Manufacturing Sector Customer',
};

export const CHARITY_ADMIN_TIME_SAVED = {
  quote:
    'IMPROVE saves me around 3–4 hours of administration time each month, while giving us the flexibility to create learning pathways tailored to different departments.',
  attribution: 'Charity Sector Customer',
};

export const MANUFACTURING_INDUCTION = {
  quote:
    'IMPROVE has become an important part of our induction programme, helping us deliver consistent training to every new starter.',
  attribution: 'Manufacturing Sector Customer',
};

/**
 * Already on /solutions/onboarding before this brief, from a named customer.
 * Kept, and now sits beside Steve's induction quote rather than instead of it.
 */
export const ADVOCACY_FOCUS_ONBOARDING = {
  quote:
    'New staff have been in ‘awe’ of our onboarding process which is all centrally delivered via IMPROVE.',
  attribution: 'Advocacy Focus',
};

export const MANUFACTURING_COURSE_RANGE = {
  quote:
    'IMPROVE gives us a great range of courses for induction, but also allows us to provide additional learning to support the ongoing training and development of our people.',
  attribution: 'Manufacturing Sector Customer',
};

export const CHARITY_COURSE_VARIETY = {
  quote:
    'The increased number and variety of courses available has also been a real positive, helping to improve engagement with training across the charity.',
  attribution: 'Charity Sector Customer',
};

export const MANUFACTURING_OWN_CONTENT = {
  quote:
    "We particularly value the ability to upload our own learning materials alongside IMPROVE's courses.",
  attribution: 'Manufacturing Sector Customer',
};

export const CHARITY_REPORTING = {
  quote:
    "The reporting functionality gives managers greater visibility of their teams' training and allows them to access the relevant reports.",
  attribution: 'Charity Sector Customer',
};

/**
 * The short form of CHARITY_ADMIN_TIME_SAVED. Steve supplied both: the long one
 * carries the homepage, the short one sits under the reporting quote where the
 * pathways half would repeat what that section already says.
 */
export const CHARITY_ADMIN_TIME_SAVED_SHORT = {
  quote: 'IMPROVE saves me around 3–4 hours of administration time each month.',
  attribution: 'Charity Sector Customer',
};

/**
 * Steve asked for this one to read as a PARTNER testimonial rather than a
 * customer one, hence the eyebrow and the second attribution line.
 */
export const HRCENTRAL_PARTNER = {
  eyebrow: 'Partner Testimonial',
  quote:
    'The system is like no other in the market. HRCentral Limited and our clients are continually impressed by the user-friendly, flexible and limitless training opportunities IMPROVE can offer.',
  attribution: 'HRCentral Limited',
  role: 'IMPROVE Partner',
};

/**
 * Supplied in the same brief but NOT placed yet: Steve headed these "Charity
 * Sector Page" and the site has no sector pages, only "case study coming soon"
 * cards on /customer-stories. Held deliberately, pending his answer on whether
 * to build a charity page or fold them into Customer Stories. Exported so the
 * wording is already transcribed when he decides.
 */
export const CHARITY_VALUE_FOR_MONEY = {
  quote:
    'Overall, we feel the platform offers excellent value for money compared with other providers we have considered, making it a great option for charities looking for a user-friendly and cost-effective training solution.',
  attribution: 'Charity Sector Customer',
};

export const CHARITY_LEARNING_PATHWAYS = {
  quote:
    'The ability to create individual and team learning pathways has significantly reduced confusion around which courses users need to complete, while also making the platform much more user-friendly.',
  attribution: 'Charity Sector Customer',
};

/**
 * Trilogy Active, 16 September 2026, the first customer quoted by name.
 * Attribution exactly as Steve supplied it.
 */
const TRILOGY = 'Head of People and Development';
const TRILOGY_ROLE = 'Trilogy Active, Not for Profit Sector';

export const TRILOGY_EASY = {
  quote: "It's easy for us to manage and easy for our people to use, which is what you want from a learning platform.",
  attribution: TRILOGY,
  role: TRILOGY_ROLE,
};

export const TRILOGY_SIMPLER = {
  quote:
    "It's made training much simpler for us. We've got consistency across all our sites, it's easier to keep track of mandatory training and it's helped make onboarding much more straightforward.",
  attribution: TRILOGY,
  role: TRILOGY_ROLE,
};

export const TRILOGY_ONBOARDING = {
  quote: "It's helped make onboarding much more straightforward.",
  attribution: TRILOGY,
  role: TRILOGY_ROLE,
};

export const TRILOGY_BITE_SIZED = {
  quote:
    "The modules are bite-sized, and some are short microlearning pieces, so people aren't having to sit through long training sessions.",
  attribution: TRILOGY,
  role: TRILOGY_ROLE,
};

export const TRILOGY_CPD = {
  quote: 'It also gives us the opportunity to support professional qualifications and CPD.',
  attribution: TRILOGY,
  role: TRILOGY_ROLE,
};

export const TRILOGY_COMPLIANCE = {
  quote: "We've got consistency across all our sites, and it's easier to keep track of mandatory training.",
  attribution: TRILOGY,
  role: TRILOGY_ROLE,
};

export const TRILOGY_VALUE = {
  quote:
    "It has also saved us money compared with some of the training solutions we've used previously, while helping us develop a better-skilled team.",
  attribution: TRILOGY,
  role: TRILOGY_ROLE,
};

export const TRILOGY_SUPPORT = {
  quote:
    "The IT support team are quick and helpful, which makes a difference when you're managing learning across a whole organisation.",
  attribution: TRILOGY,
  role: TRILOGY_ROLE,
};

export const TRILOGY_RENEWED = {
  quote:
    "We've renewed because it works well for us. It's flexible, easy to use and engaging for our people. It works alongside our existing mandatory training, but also gives us the opportunity to support professional qualifications and CPD.",
  attribution: TRILOGY,
  role: TRILOGY_ROLE,
};

/** The Customer Stories card: the facts Steve supplied with the quote. */
export const TRILOGY_STORY = {
  company: 'Trilogy Active',
  sector: 'Not-for-Profit / Health, Wellbeing & Leisure',
  facts: '12 sites | 250 people',
  ...TRILOGY_SIMPLER,
};

/**
 * Highland Alarms Ltd, supplied by Steve by email (25 Sep 2026): a Direct
 * customer that has just renewed for another three years. Attribution exactly
 * as supplied. Placement as he asked: consistency on the homepage, "worth
 * serious consideration" on the platform page, the renewal on Pricing, Buy
 * IMPROVE and Customer Stories (in the logo carousel).
 */
const HIGHLAND = 'Business Operations Director';
const HIGHLAND_ROLE = 'Highland Alarms Ltd';

export const HIGHLAND_CONSISTENCY = {
  quote:
    'IMPROVE has given us greater consistency in how we train people, a smoother onboarding process and much clearer visibility of compliance. We can demonstrate our training records with confidence when clients, auditors or certification bodies ask.',
  attribution: HIGHLAND,
  role: HIGHLAND_ROLE,
};

export const HIGHLAND_WORTH_CONSIDERING = {
  quote:
    'If you want a simple, consistent way to onboard staff, keep on top of compliance training and support development, IMPROVE is worth serious consideration for SMEs.',
  attribution: HIGHLAND,
  role: HIGHLAND_ROLE,
};

export const HIGHLAND_RENEWED = {
  quote:
    'IMPROVE does what we need it to do, reliably, and has become part of how we carry out in-house training. The content, ease of use and reporting have all delivered value, which is why we’ve renewed for a further three years.',
  attribution: HIGHLAND,
  role: HIGHLAND_ROLE,
};

/**
 * East Midlands Chamber, supplied by Steve (25 Sep 2026), headed "Trusted by
 * East Midlands Chamber since 2019", which becomes the eyebrow. On the
 * homepage, Pricing, Buy IMPROVE and Customer Stories (in the logo carousel).
 */
export const EAST_MIDLANDS_CHAMBER = {
  eyebrow: 'Trusted by East Midlands Chamber since 2019',
  quote:
    'IMPROVE adds significant value to the Chamber’s operations, from a workforce development and compliance perspective. The interactive training provides a one-stop-shop tool for businesses to address regulatory, legislative and skills development needs. The Chamber has used IMPROVE for many years and it’s a mandatory part of our business toolkit.',
  attribution: 'Scott Knowles, Chief Executive',
  role: 'East Midlands Chamber',
};

/**
 * The testimonial carousel (Pricing, Customer Stories, Watch Demo since
 * 18 Sep 2026). Headline + quote per card, and a logo where the customer has
 * one. The Trilogy Active logo is their public brand mark, sized to the same
 * 50px row as the others (Steve, 18 Sep 2026: "all other customer
 * testimonials have their logo").
 */
export const CAROUSEL_TESTIMONIALS = [
  // The two named testimonials of 25 Sep 2026 lead. Logos are self-hosted
  // (a third-party image fails the prerender gate): the Chamber's own logo
  // cropped to its ink; Highland Alarms' own logo, unaltered, on their navy
  // (their wordmark is white), strapline cropped as it is unreadable at 50px.
  {
    eyebrow: EAST_MIDLANDS_CHAMBER.eyebrow,
    headline: 'A Mandatory Part of Our Business Toolkit',
    quote: EAST_MIDLANDS_CHAMBER.quote,
    company: 'East Midlands Chamber',
    logo: '/static/homepage/testimonial/east-midlands-chamber-testimonial.png',
    logoAlt: 'East Midlands Chamber',
    attribution: EAST_MIDLANDS_CHAMBER.attribution,
    role: EAST_MIDLANDS_CHAMBER.role,
  },
  {
    headline: 'Renewed for a Further Three Years',
    quote: HIGHLAND_RENEWED.quote,
    company: 'Highland Alarms Ltd',
    logo: '/static/homepage/testimonial/highland-alarms-testimonial.png',
    logoAlt: 'Highland Alarms Ltd',
    attribution: HIGHLAND,
    role: HIGHLAND_ROLE,
  },
  {
    headline: 'Saved Us Money',
    quote: TRILOGY_VALUE.quote,
    company: 'Trilogy Active',
    logo: '/static/homepage/testimonial/trilogy-active-testimonial.png',
    logoAlt: 'Trilogy Active',
    attribution: TRILOGY,
    role: TRILOGY_ROLE,
  },
  {
    headline: 'The Best Onboarding Process',
    quote:
      "New staff have been in ‘awe’ of our onboarding process which is all centrally delivered via IMPROVE. We have found it to be the best onboarding process we have experienced.",
    company: 'Advocacy Focus',
    logo: '/static/homepage/testimonial/advocacy-focus-testimonial.png',
    logoAlt: 'Advocacy Focus Approved IMPROVE partner',
  },
  {
    headline: 'Comprehensive & Easy to Access',
    quote:
      "It’s authoritative, accurate, comprehensive, and easy to access. It removes the stress of training delivery from business owners, knowing that they can review what their staff are learning.",
    company: 'Smart Space',
    logo: '/static/homepage/testimonial/smart-space-testimonial.png',
    logoAlt: 'Smart Space Approved IMPROVE partner',
  },
  {
    headline: 'Seamless Training Process',
    quote:
      "The user-friendly interface has been built to ensure that everyone can use it with ease, and the courses are tailored to suit each team member's learning needs.",
    company: 'Eden Holistic Pet Foods',
    logo: '/static/homepage/testimonial/eden-holistic-pet-foods-testimonial.png',
    logoAlt: 'Eden Holistic Pet Foods Approved IMPROVE partner',
  },
];
